import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Button,
  Tooltip,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { format, parseISO, isSameDay } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import api from '../../services/api';
import { toast } from 'react-toastify';
import KanbanConversationDialog from './KanbanConversationDialog';

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(0.8),
    background: '#fff',
    borderRadius: 10,
    border: `1px solid ${theme.mode === 'light' ? 'rgba(15, 61, 46, 0.10)' : 'rgba(255,255,255,0.10)'}`,
    boxShadow: theme.mode === 'light'
      ? '0 4px 14px rgba(15, 61, 46, 0.07)'
      : '0 4px 14px rgba(0, 0, 0, 0.24)',
    marginBottom: theme.spacing(1),
    cursor: 'grab',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    marginRight: theme.spacing(0.5),
    transition: 'border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease',
    '&:hover': {
      borderColor: theme.mode === 'light' ? 'rgba(0, 135, 90, 0.28)' : 'rgba(74, 222, 128, 0.28)',
      boxShadow: theme.mode === 'light'
        ? '0 7px 18px rgba(15, 61, 46, 0.11)'
        : '0 7px 18px rgba(0, 0, 0, 0.30)',
      transform: 'translateY(-1px)',
    },
    '@media (max-height: 850px)': {
      padding: 5,
      marginBottom: 6,
      borderRadius: 7,
    },
  },
  cardLocked: {
    cursor: 'default',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    justifyContent: 'space-between',
    '@media (max-height: 850px)': {
      marginBottom: 2,
    },
  },
  leftHeader: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
    flexShrink: 0,
  },
  valueChip: {
    appearance: 'none',
    height: 22,
    maxWidth: 88,
    padding: '0 7px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    borderRadius: 9,
    border: '1px solid rgba(0, 135, 90, 0.18)',
    background: 'rgba(0, 135, 90, 0.08)',
    color: '#006B4A',
    fontFamily: 'inherit',
    fontSize: '0.64rem',
    fontWeight: 800,
    lineHeight: 1,
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background 0.15s ease, border-color 0.15s ease',
    '&:hover': {
      background: 'rgba(0, 135, 90, 0.14)',
      borderColor: 'rgba(0, 135, 90, 0.30)',
    },
    '&:focus-visible': {
      outline: '2px solid rgba(0, 135, 90, 0.32)',
      outlineOffset: 1,
    },
    '@media (max-height: 850px)': {
      height: 20,
      maxWidth: 82,
      padding: '0 6px',
      fontSize: '0.59rem',
    },
  },
  avatar: {
    marginRight: theme.spacing(1),
    width: theme.spacing(3),
    height: theme.spacing(3),
    '@media (max-height: 850px)': {
      width: 20,
      height: 20,
      marginRight: 6,
      fontSize: '0.65rem',
    },
  },
  cardTitle: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: theme.mode === "light" ? theme.palette.text.primary : "#000",
    '@media (max-height: 850px)': {
      fontSize: '0.86rem',
    },
  },
  ticketNumber: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    color: "#757575",
    background: '#F1F4F3',
    borderRadius: 8,
    padding: '2px 6px',
    '@media (max-height: 850px)': {
      fontSize: '0.72rem',
    },
  },
  divider: {
    background: "#e6e6e6",
    marginTop: 1,
  },
  lastMessageTime: {
    fontSize: '0.8rem',
    color: "#757575",
    '@media (max-height: 850px)': {
      fontSize: '0.7rem',
    },
  },
  lastMessageTimeUnread: {
    fontSize: '0.8rem',
    color: theme.palette.success.main,
    fontWeight: 'bold',
    '@media (max-height: 850px)': {
      fontSize: '0.7rem',
    },
  },
  cardDescription: {
    fontSize: '0.9rem',
    color: "#757575",
    flexGrow: 1,
    marginRight: theme.spacing(1),
    '@media (max-height: 850px)': {
      fontSize: '0.78rem',
      marginRight: 6,
    },
  },
  valueRow: {
    display: 'none',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    '@media (max-height: 850px)': {
      marginTop: 3,
    },
  },
  descriptionRow: {
    display: 'none',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '@media (max-height: 850px)': {
      marginBottom: 3,
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(0.75),
    paddingTop: theme.spacing(0.75),
    borderTop: '1px solid #EEF1F0',
    gap: 4,
    '@media (max-height: 850px)': {
      gap: 2,
    },
  },
  verConversaButton: {
    fontSize: '0.5rem',
    padding: '2px 8px',
    fontWeight: 700,
    textTransform: 'none',
    color: '#fff',
    backgroundColor: '#00875A',
    borderRadius: '10px',
    '@media (max-height: 850px)': {
      padding: '1px 6px',
    },
    '&:hover': {
      backgroundColor: '#006644',
    },
  },
  arrowButton: {
    padding: 2,
    color: '#6B778C',
    '&:hover': {
      color: '#0d47a1',
    },
  },
  connectionTag: {
    background: 'rgba(0, 135, 90, 0.10)',
    color: '#006B4A',
    border: '1px solid rgba(0, 135, 90, 0.14)',
    padding: '2px 6px',
    fontWeight: 'bold',
    borderRadius: '10px',
    fontSize: '0.5rem',
    marginLeft: 'auto',
    '@media (max-height: 850px)': {
      padding: '1px 5px',
      fontSize: '0.46rem',
    },
  },
  opportunityValue: {
    fontSize: '0.9rem',
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    cursor: 'pointer',
    '@media (max-height: 850px)': {
      fontSize: '0.78rem',
    },
  },
  removeValueButton: {
    padding: 0,
    marginLeft: theme.spacing(1),
    color: theme.palette.error.main,
  },
  editCardBtn: {
    display: 'none',
    padding: 2,
    color: '#6B778C',
    '&:hover': { color: '#0052CC' },
  },
  deleteCardBtn: {
    padding: 2,
    color: '#97A0AF',
    '&:hover': { color: '#991B1B' },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
  },
  dialogPaper: {
    borderRadius: '10px',
  },
  dialogButton: {
    borderRadius: '10px',
  },
}));

const KanbanCard = ({ ticket, index, updateTicket, isSystem }) => {
  const classes = useStyles();
  const history = useHistory();

  const [open, setOpen] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);

  const handleOpenMessages = (e) => {
    e.stopPropagation();
    setMessagesOpen(true);
  };

  const handleCloseMessages = () => {
    setMessagesOpen(false);
  };

  const handleGoToTicket = (e) => {
    e.stopPropagation();
    history.push(`/tickets/${ticket.uuid}`);
  };

  const lastMessageTimeClass =
    Number(ticket.unreadMessages) > 0
      ? classes.lastMessageTimeUnread
      : classes.lastMessageTime;

  const customFields = ticket.contact.extraInfo || [];
  const valueFieldIndex = customFields.findIndex(field => field.name === 'valor');
  const valueField = valueFieldIndex !== -1 ? customFields[valueFieldIndex] : null;
  const opportunityValue = valueField ? parseFloat(valueField.value) : null;

  const handleOpenModal = () => {
    setNewValue(valueField ? valueField.value.toString() : '');
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const updateContactValue = async (contactId, value) => {
    try {
      await api.put(`/contacts/${contactId}`, {
        extraInfo: [{ name: 'valor', value: value.toString() }],
      });
    } catch (error) {
      console.error('Erro ao atualizar o valor:', error);
    }
  };

  const removeContactValue = async () => {
    try {
      await api.put(`/contacts/${ticket.contact.id}`, {
        extraInfo: [],
      });
      if (valueFieldIndex !== -1) {
        customFields.splice(valueFieldIndex, 1);
      }
      updateTicket({ ...ticket });
    } catch (error) {
      console.error('Erro ao remover o valor:', error);
    }
  };

  const handleSaveValue = async () => {
    await updateContactValue(ticket.contact.id, newValue);
    if (valueField) {
      valueField.value = newValue;
    } else {
      if (!ticket.contact.extraInfo) {
        ticket.contact.extraInfo = [];
      }
      ticket.contact.extraInfo.push({ name: 'valor', value: newValue });
    }
    updateTicket({ ...ticket });
    setOpen(false);
  };

  const handleDeleteFromKanban = async () => {
    try {
      await api.delete(`/ticket-tags/${ticket.id}`);
      updateTicket({ ...ticket, tags: [] });
      toast.success('Card removido do kanban');
    } catch (error) {
      console.error('Erro ao remover card:', error);
      toast.error('Erro ao remover card');
    }
    setConfirmDelete(false);
  };

  return (
    <>
      <Draggable draggableId={ticket.id.toString()} index={index} isDragDisabled={!!isSystem}>
        {(provided, snapshot) => (
          <div
            className={`${classes.card}${isSystem ? ' ' + classes.cardLocked : ''}`}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...(isSystem ? {} : provided.dragHandleProps)}
          >
            <div className={classes.header}>
              <div className={classes.leftHeader}>
                <Avatar src={ticket.contact.urlPicture} className={classes.avatar} style={{ backgroundColor: !ticket.contact.urlPicture ? '#3f51b5' : undefined, color: '#fff', fontWeight: 'bold' }}>
                  {!ticket.contact.urlPicture && ticket.contact.name
                    ? ticket.contact.name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join('')
                    : null}
                </Avatar>
                <Tooltip title={ticket.contact.name}>
                  <Typography className={classes.cardTitle}>
                    {ticket.contact.name?.substring(0, 10) || ' '}
                  </Typography>
                </Tooltip>
              </div>
              <div className={classes.headerActions}>
                {!isSystem && (
                  <button
                    type="button"
                    className={classes.valueChip}
                    title={opportunityValue !== null ? 'Editar valor' : 'Atribuir valor'}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenModal();
                    }}
                  >
                    {opportunityValue !== null
                      ? opportunityValue.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                          notation: 'compact',
                          maximumFractionDigits: 1,
                        })
                      : '+ Valor'}
                  </button>
                )}
                <Typography className={classes.ticketNumber}>
                  #{ticket.id}
                </Typography>
              </div>
            </div>
            <Divider className={classes.divider} />
            {!isSystem && (
              <div className={classes.valueRow}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    className={classes.opportunityValue}
                    onClick={handleOpenModal}
                  >
                    {opportunityValue !== null
                      ? `Valor: R$ ${opportunityValue.toFixed(2)}`
                      : 'Atribuir Valor'}
                  </Typography>
                  {opportunityValue !== null && (
                    <Tooltip title={"Remover"}>
                      <IconButton
                        className={classes.removeValueButton}
                        onClick={removeContactValue}
                        size="small"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
            )}
            <div className={classes.descriptionRow}>
              <Tooltip title={(() => { const lm = ticket.lastMessage; if (!lm) return ' '; if (lm.includes('data:image/png;base64')) { if (lm.includes('maps.google.com')) return '📍 Localização'; const p = lm.split('|'); return p[p.length-1]?.trim() || '📢 Anúncio'; } return lm; })() || ' '}>
                <Typography className={classes.cardDescription}>
                  {(() => { const lm = ticket.lastMessage; if (!lm) return ' '; if (lm.includes('data:image/png;base64')) { if (lm.includes('maps.google.com')) return '📍 Localização'; const p = lm.split('|'); const t = p[p.length-1]?.trim(); return t ? t.substring(0, 20) : '📢 Anúncio'; } return lm.substring(0, 20); })() || ' '}
                </Typography>
              </Tooltip>
              <Typography className={lastMessageTimeClass}>
                {isSameDay(parseISO(ticket.updatedAt), new Date())
                  ? format(parseISO(ticket.updatedAt), 'HH:mm')
                  : format(parseISO(ticket.updatedAt), 'dd/MM/yyyy')}
              </Typography>
            </div>
            <div className={classes.footer}>
              {/* Ver Conversa: abre dialog inline com a conversa */}
              <Button
                size="small"
                className={classes.verConversaButton}
                onClick={handleOpenMessages}
              >
                Ver Conversa
              </Button>
              {/* Seta: vai para /tickets (atendimentos) */}
              <Tooltip title="Ir para Atendimentos">
                <IconButton size="small" className={classes.arrowButton} onClick={handleGoToTicket}>
                  <ArrowForwardIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {!isSystem && (
                <>
                  <Tooltip title="Editar valor">
                    <IconButton size="small" className={classes.editCardBtn} onClick={handleOpenModal}>
                      <EditIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remover do kanban">
                    <IconButton size="small" className={classes.deleteCardBtn} onClick={() => setConfirmDelete(true)}>
                      <DeleteIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {ticket.user && (
                <Typography className={classes.connectionTag}>
                  {ticket.user.name.toUpperCase()}
                </Typography>
              )}
            </div>

            {/* Edit value dialog */}
            <Dialog open={open} onClose={handleCloseModal} classes={{ paper: classes.dialogPaper }}>
              <DialogTitle>{valueField ? 'Editar' : 'Atribuir'} Valor da Oportunidade</DialogTitle>
              <DialogContent>
                <TextField
                  label="Valor"
                  type="number"
                  fullWidth
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  variant="outlined"
                  size="small"
                  className={classes.textField}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseModal} color="secondary" variant="outlined" className={classes.dialogButton}>Cancelar</Button>
                <Button onClick={handleSaveValue} color="primary" variant="outlined" className={classes.dialogButton}>Salvar</Button>
              </DialogActions>
            </Dialog>

            {/* Confirm delete dialog */}
            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} classes={{ paper: classes.dialogPaper }}>
              <DialogTitle>Remover do Kanban</DialogTitle>
              <DialogContent>
                <Typography>Deseja remover este card do kanban?</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmDelete(false)} color="secondary" variant="outlined" className={classes.dialogButton}>Cancelar</Button>
                <Button onClick={handleDeleteFromKanban} style={{ color: '#fff', backgroundColor: '#BF2600' }} variant="contained" className={classes.dialogButton}>Remover</Button>
              </DialogActions>
            </Dialog>
          </div>
        )}
      </Draggable>

      {/* KanbanConversationDialog: abre a conversa inline sem sair do kanban */}
      {messagesOpen && (
        <KanbanConversationDialog
          open={messagesOpen}
          onClose={handleCloseMessages}
          ticket={ticket}
        />
      )}
    </>
  );
};

export default KanbanCard;
